import { domain } from 'modules/data';
import { AsyncDataProvider } from 'modules/data-providers';
import { useSelector } from 'modules/react-redux';
import { useGetUserCompanyById } from 'modules/utils';
import { memo, useCallback } from 'react';
import { useLazyGetNetSuiteAccounts } from 'shared/data/webApp/v1';

import { getRequest } from '../../../../selectors/pageSelectors';
import { getRequestEditMode } from '../../../../selectors/requestSelectors';
import { NetSuiteAccountsCorporateCardDataProviderProps } from './NetSuiteAccountsCorporateCardDataProvider.types';

export const NetSuiteAccountsCorporateCardDataProvider = memo<NetSuiteAccountsCorporateCardDataProviderProps>(
    (props) => {
        const { currency, children } = props;

        const request = useSelector(getRequest);
        const editMode = useSelector((state) => getRequestEditMode(state, request));

        const { id: requestId, companyId } = request;

        const company = useGetUserCompanyById(companyId);
        const isFilteredByCurrency = company?.betaFeatures.includes(domain.CompanyBetaFeature.NetSuiteRefDataCache);

        const [getNetSuiteAccounts] = useLazyGetNetSuiteAccounts();

        const onLoad = useCallback(
            async (filterText: string | null) => {
                try {
                    const response = await getNetSuiteAccounts({
                        params: {
                            query: {
                                companyId,
                                group: domain.NetSuiteAccountTypeGroup.CreditCard,
                                query: filterText ?? undefined,
                            },
                        },
                    });

                    const values =
                        response.values.map((value) => ({
                            id: value.id,
                            text: value.name,
                            currency: domain.schemas.mapReference(value.currency),
                        })) || [];

                    return isFilteredByCurrency
                        ? values.filter((account) => {
                              return !currency || !account.currency || account.currency.id === currency.id;
                          })
                        : values;
                } catch {
                    return [];
                }
            },
            [companyId, currency, isFilteredByCurrency, getNetSuiteAccounts]
        );

        return (
            <AsyncDataProvider
                cacheStorageId={
                    isFilteredByCurrency
                        ? `requestOptions.netSuiteAccounts.${requestId}.${editMode}.${currency?.id}`
                        : `requestOptions.netSuiteAccounts.${requestId}.${editMode}`
                }
                pageSize={100}
                onLoad={onLoad}
            >
                {children}
            </AsyncDataProvider>
        );
    }
);

NetSuiteAccountsCorporateCardDataProvider.displayName = 'NetSuiteAccountsCorporateCardDataProvider';
